import { shouldIncludeCredentialsInRequest } from './images';

interface IUploadFileRequest {
  file: File;
  signedUrl: string;
}
interface IUploadFileResponse {
  success: boolean;
  message?: string;
}
export const uploadFile = async (
  props: IUploadFileRequest
): Promise<IUploadFileResponse> => {
  const { file, signedUrl } = props;
  try {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      credentials: shouldIncludeCredentialsInRequest(signedUrl)
        ? 'include'
        : undefined,
    });
    if (response.ok) {
      console.log('File uploaded successfully');
      return { success: true };
    } else {
      console.error('Upload failed:', response.statusText);
      return {
        success: false,
        message: `Upload failed: ${response.statusText}`,
      };
    }
  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      message: `Upload error: ${error}`,
    };
  }
};

// =============================================================

// Same PUT contract as uploadFile, but uses XHR so callers can track
// upload percent (fetch has no upload progress) and cancel via AbortSignal.
interface IUploadFileV2Request {
  file: File;
  signedUrl: string;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

export const uploadFileV2 = (
  props: IUploadFileV2Request
): Promise<IUploadFileResponse & { aborted?: boolean }> => {
  const { file, signedUrl, onProgress, signal } = props;

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();

    const cleanup = () => {
      signal?.removeEventListener('abort', abortXhr);
    };

    const abortXhr = () => {
      xhr.abort();
    };

    xhr.open('PUT', signedUrl);
    xhr.withCredentials = shouldIncludeCredentialsInRequest(signedUrl);

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress?.(Math.round((event.loaded / event.total) * 100));
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ success: true });
        return;
      }
      resolve({
        success: false,
        message: `Upload failed: ${xhr.statusText}`,
      });
    };

    xhr.onerror = () => {
      cleanup();
      resolve({
        success: false,
        message: 'Upload error: network error',
      });
    };

    // User cancelled via AbortSignal — not a failure; check aborted before toast.
    xhr.onabort = () => {
      cleanup();
      resolve({
        success: false,
        aborted: true,
      });
    };

    // Already aborted before send — resolve without starting the PUT.
    if (signal?.aborted) {
      cleanup();
      resolve({
        success: false,
        aborted: true,
      });
      return;
    }
    signal?.addEventListener('abort', abortXhr);
    xhr.send(file);
  });
};

// =============================================================
